import { Mandate } from '../../interfaces/client/mandate.interface';

import {
  LoadMandateAction,
  OnComplete,
  RemoveMandateAction,
  SaveMandateAction,
  UpdateMandateAction,
  LoadMandatesAction
} from './mandate.types';

export class MandateActions {
  static readonly LOAD_MANDATES: string = '[MANDATE_STORE] Load mandates';
  static readonly SET_MANDATES: string = '[MANDATE_STORE] Set mandates';

  static readonly LOAD_MANDATE: string = '[MANDATE_STORE] Load mandate';
  static readonly SET_MANDATE: string = '[MANDATE_STORE] Set mandate';

  static readonly SAVE_MANDATE: string = '[MANDATE_STORE] Save mandate';
  static readonly UPDATE_MANDATE: string = '[MANDATE_STORE] Update mandate';
  static readonly REMOVE_MANDATE: string = '[MANDATE_STORE] Remove mandate';

  // v3 redesign — top-level create (no client_id nesting).  Distinct from
  // saveMandate (which is the legacy client-scoped flow).
  static readonly CREATE_MANDATE: string = '[MANDATE_STORE] Create mandate';
  static readonly DELETE_MANDATE: string = '[MANDATE_STORE] Delete mandate';

  // v3 — paper_trail version history rendered on MandateDetail's Activity tab.
  static readonly LOAD_MANDATE_VERSIONS: string = '[MANDATE_STORE] Load mandate versions';
  static readonly SET_MANDATE_VERSIONS: string = '[MANDATE_STORE] Set mandate versions';

  // v3 — Active Storage attachments on the Documents tab.
  static readonly LOAD_MANDATE_DOCUMENTS: string = '[MANDATE_STORE] Load mandate documents';
  static readonly SET_MANDATE_DOCUMENTS: string = '[MANDATE_STORE] Set mandate documents';
  static readonly ATTACH_MANDATE_DOCUMENT: string = '[MANDATE_STORE] Attach mandate document';
  static readonly DETACH_MANDATE_DOCUMENT: string = '[MANDATE_STORE] Detach mandate document';

  static loadMandates = (onComplete: OnComplete): LoadMandatesAction => ({
    type: MandateActions.LOAD_MANDATES,
    onComplete
  });

  static saveMandate = (
    id: number | string,
    payload: Mandate,
    onComplete: OnComplete
  ): SaveMandateAction => ({
    type: MandateActions.SAVE_MANDATE,
    id,
    payload,
    onComplete
  });

  static loadMandate = (id: number | string, onComplete: OnComplete): LoadMandateAction => ({
    type: MandateActions.LOAD_MANDATE,
    id,
    onComplete
  });

  static updateMandate = (
    id: number | string,
    payload: Mandate,
    onComplete: OnComplete
  ): UpdateMandateAction => ({
    type: MandateActions.UPDATE_MANDATE,
    id,
    payload,
    onComplete
  });

  static removeMandate = (id: number | string, onComplete: OnComplete): RemoveMandateAction => ({
    type: MandateActions.REMOVE_MANDATE,
    id,
    onComplete
  });

  /**
   * v3: delete via the top-level DELETE /mandates/:id.
   *
   * Distinct from `removeMandate`, which builds the client-scoped
   * /clients/:id/mandates/:mandate_id URL and therefore needs both ids — the
   * v3 lists only know the mandate.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static deleteMandate = (id: number | string, onComplete: OnComplete): any => ({
    type: MandateActions.DELETE_MANDATE,
    id,
    onComplete
  });

  /**
   * v3 top-level mandate create.  Calls POST /mandates with the full payload
   * (client_id is part of the body rather than the URL).  `onComplete`
   * receives the created mandate so the caller can navigate to its detail.
   */
  static createMandate = (payload: Mandate, onComplete: (created?: Mandate) => void) => ({
    type: MandateActions.CREATE_MANDATE,
    payload,
    onComplete
  });

  /**
   * v3 — fetch paper_trail version history for a single mandate.  Saga puts
   * SET_MANDATE_VERSIONS with the array keyed by mandate id; reducer stores
   * under mandate_store.versionsByMandate[id] so the Activity tab can read
   * it without dispatching repeatedly on tab switches.
   */
  static loadMandateVersions = (id: number | string, onComplete: OnComplete) => ({
    type: MandateActions.LOAD_MANDATE_VERSIONS,
    id,
    onComplete
  });

  /** v3 — GET /mandates/:id/documents → attached files on the Documents tab. */
  static loadMandateDocuments = (id: number | string, onComplete: OnComplete) => ({
    type: MandateActions.LOAD_MANDATE_DOCUMENTS,
    id,
    onComplete
  });

  /**
   * v3 — POST /mandates/:id/documents with a base64 data-URL `data` field
   * (matches the legacy ImageHelper.create_attachment_for_image format).
   * onComplete fires with the full refreshed attachment list.
   */
  static attachMandateDocument = (
    id: number | string,
    payload: { name: string; data: string },
    onComplete: OnComplete
  ) => ({
    type: MandateActions.ATTACH_MANDATE_DOCUMENT,
    id,
    payload,
    onComplete
  });

  /** v3 — DELETE /mandates/:id/documents/:attachment_id (purges the blob). */
  static detachMandateDocument = (
    id: number | string,
    attachmentId: number | string,
    onComplete: OnComplete
  ) => ({
    type: MandateActions.DETACH_MANDATE_DOCUMENT,
    id,
    attachmentId,
    onComplete
  });
}
